import { registerTool } from '../ToolRegistry'
import { getSharedToolRegistration } from '../sharedToolCatalog'
import { useCampaignStore } from '@/stores/campaignStore'
import { useCampaignResourceStore } from '@/stores/campaignResourceStore'
import { useUserStore } from '@/stores/userStore'
import { useVaultStore } from '@/components/plugins/KnowledgeBase/stores/vaultStore'
import NoteService from '@/components/plugins/KnowledgeBase/services/NoteService'
import * as noteResolution from '../../../../../shared/ai/noteResolution.js'
import { db } from '@/services/firebase/config'
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp
} from 'firebase/firestore'
import PermissionService, { PermissionLevel } from '@/services/firebase/PermissionService'

const {
  buildNoteMatches,
  buildNoteUpdateFields,
  formatNoteChoice,
  listAvailableVaultNames,
  normalizeVaultName,
  parseNoteUpdateRequest,
  resolveNoteMatch,
  resolveVaultByName
} = noteResolution

const GENERIC_NOTE_TITLES = new Set([
  'campaign idea',
  'campaign notes',
  'idea',
  'ideas',
  'new note',
  'note',
  'notes',
  'session note',
  'untitled'
])

const LOW_SIGNAL_NOTE_CONTENT_PHRASES = new Set([
  'approved',
  'campaign idea',
  'do it',
  'find the idea',
  'go ahead',
  'make it',
  'ok',
  'okay',
  'sure',
  'that idea',
  'that one',
  'the idea',
  'this one',
  'use it',
  'use that',
  'you find the idea'
])

const LOW_SIGNAL_NOTE_WORDS = new Set([
  'a',
  'an',
  'anything',
  'approved',
  'campaign',
  'create',
  'do',
  'find',
  'go',
  'idea',
  'it',
  'make',
  'note',
  'ok',
  'okay',
  'one',
  'something',
  'sure',
  'that',
  'the',
  'this',
  'up',
  'use',
  'with',
  'write',
  'yes',
  'you'
])

function normalizeNoteDraftText(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[^a-z0-9\s']/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function tokenizeNoteDraftText(value = '') {
  const normalized = normalizeNoteDraftText(value)
  return normalized ? normalized.split(' ').filter(Boolean) : []
}

function isLowSignalNoteContent(content = '') {
  const normalized = normalizeNoteDraftText(content)
  if (!normalized) return true
  if (LOW_SIGNAL_NOTE_CONTENT_PHRASES.has(normalized)) return true

  const tokens = tokenizeNoteDraftText(content)
  if (!tokens.length) return true

  if (tokens.length <= 5 && tokens.every((token) => LOW_SIGNAL_NOTE_WORDS.has(token))) {
    return true
  }

  if (
    tokens.length <= 8 &&
    /^(?:you\s+)?(?:find|make|create|write|invent|use|do)\b/.test(normalized) &&
    /\b(?:idea|note|one|something|anything|it|that|this)\b/.test(normalized)
  ) {
    return true
  }

  return false
}

function validateCreateNoteDraft(args = {}) {
  const title = String(args?.title || '').trim()
  const content = String(args?.content || '').trim()
  if (!title || !content) return { ok: true }

  const normalizedTitle = normalizeNoteDraftText(title)
  const normalizedContent = normalizeNoteDraftText(content)
  const genericTitle = GENERIC_NOTE_TITLES.has(normalizedTitle)
  const tooVague =
    isLowSignalNoteContent(content) || (genericTitle && normalizedTitle === normalizedContent)

  if (!tooVague) return { ok: true }

  return {
    ok: false,
    error: 'note_content_too_vague',
    message: 'I still need the actual campaign idea or note details before I can create that note.'
  }
}

export function registerNoteTools() {
  const loadSearchPool = async ({ source = '', vaultName = null } = {}) => {
    const normalizedSource = String(source || '').toLowerCase()
    const includeCampaign = normalizedSource !== 'knowledgebase' && !vaultName
    const includeKnowledgebase = normalizedSource !== 'campaign'
    const pool = []
    let vaultResolution = null

    if (includeCampaign) {
      const cs = useCampaignStore()
      const current =
        (typeof cs?.getCurrentCampaign === 'function'
          ? cs.getCurrentCampaign()
          : cs?.currentCampaign) || null
      const campaignId = current && (current.id || current.value?.id)

      let notes = []
      if (campaignId) {
        const crs = useCampaignResourceStore()
        try {
          const accessible = crs.getAccessibleResources(campaignId) || []
          notes = accessible.filter((r) => r.resourceType === 'notes')
        } catch {
          /* ignore */
        }
        if (!Array.isArray(notes) || notes.length === 0) {
          try {
            const snap = await getDocs(
              query(collection(db, 'campaigns', campaignId, 'notes'), orderBy('updatedAt', 'desc'))
            )
            notes = snap.docs.map((d) => ({ id: d.id, ...d.data(), campaignId, source: 'campaign' }))
          } catch {
            /* ignore */
          }
        } else {
          notes = notes.map((note) => ({ ...note, campaignId, source: 'campaign' }))
        }
      }

      for (const note of Array.isArray(notes) ? notes : []) {
        pool.push(note)
      }
    }

    if (includeKnowledgebase) {
      const vs = useVaultStore()
      await vs.loadVaults()
      const availableVaults = listAvailableVaultNames(vs.vaults)
      const resolvedVault = vaultName ? resolveVaultByName(vs.vaults, vaultName) : null
      if (vaultName) {
        vaultResolution = {
          status: resolvedVault?.status || 'not_found',
          requestedVaultName: vaultName,
          resolvedVaultName: resolvedVault?.vault?.name || null,
          matchingVaults: Array.isArray(resolvedVault?.matches)
            ? listAvailableVaultNames(resolvedVault.matches)
            : [],
          availableVaults
        }
      }

      const kbNotesRaw = await vs.loadAllVaultsNotes()
      const kbNotes = (Array.isArray(kbNotesRaw) ? kbNotesRaw : []).map((note) => ({
        ...note,
        vaultName:
          note?.vaultName ||
          (Array.isArray(vs.vaults) ? vs.vaults.find((vault) => vault.id === note?.vaultId)?.name : null) ||
          null,
        source: 'knowledgebase'
      }))

      const scopedKbNotes =
        vaultName && resolvedVault?.vault
          ? kbNotes.filter((note) => String(note?.vaultId || '') === String(resolvedVault.vault.id))
          : vaultName
            ? []
            : kbNotes

      for (const note of scopedKbNotes) {
        pool.push(note)
      }
    }

    return { pool, vaultResolution }
  }

  const resolveNoteTarget = async (args = {}) => {
    const noteId = String(args?.id || '').trim()
    const noteQuery = String(args?.query || args?.noteName || '').trim()
    const source = String(args?.source || '').toLowerCase()
    const requestedVaultName = args?.vaultName ? String(args.vaultName) : null

    if (noteId) {
      return {
        ok: true,
        note: {
          id: noteId,
          vaultId: args?.vaultId ? String(args.vaultId) : null,
          source: source || null,
          campaignId: args?.campaignId ? String(args.campaignId) : null
        }
      }
    }

    if (!noteQuery) {
      return { ok: false, error: 'Missing note reference' }
    }

    const search = await loadSearchPool({ source, vaultName: requestedVaultName })
    const vaultResolution = search.vaultResolution || null
    if (vaultResolution?.status === 'ambiguous') {
      return {
        ok: false,
        error: 'vault_ambiguous',
        requestedVaultName,
        matchingVaults: vaultResolution.matchingVaults,
        availableVaults: vaultResolution.availableVaults
      }
    }
    if (vaultResolution?.status === 'not_found') {
      return {
        ok: false,
        error: 'vault_not_found',
        requestedVaultName,
        availableVaults: vaultResolution.availableVaults
      }
    }

    const matches = buildNoteMatches(search.pool, noteQuery, { limit: 5, excerptLength: 200 })
    const noteMatch = resolveNoteMatch(matches)
    if (noteMatch.status === 'not_found') {
      return {
        ok: false,
        error: 'note_not_found',
        requestedNoteName: noteQuery,
        requestedVaultName,
        availableNotes: []
      }
    }

    if (noteMatch.status === 'candidate') {
      return {
        ok: false,
        error: 'note_candidate',
        requestedNoteName: noteQuery,
        requestedVaultName,
        matchingNotes: matches.map(formatNoteChoice)
      }
    }

    if (noteMatch.status === 'ambiguous') {
      return {
        ok: false,
        error: 'note_ambiguous',
        requestedNoteName: noteQuery,
        requestedVaultName,
        matchingNotes: matches.map(formatNoteChoice)
      }
    }

    return {
      ok: true,
      note: {
        id: noteMatch.note.id,
        vaultId: noteMatch.note.vaultId || null,
        source: noteMatch.note.source || null,
        campaignId: noteMatch.note.campaignId || null,
        title: noteMatch.note.title || '',
        vaultName: noteMatch.note.vaultName || null
      }
    }
  }

  registerTool({
    ...getSharedToolRegistration('find_notes'),
    handler: async (args) => {
      const q = String(args?.query || '').trim()
      const lim = Number(args?.limit || 5)
      const requestedFolder = args?.folder ? String(args.folder).trim() : null
      if (!q) return { ok: false, error: 'Please provide a "query" argument.' }

      const search = await loadSearchPool({
        source: String(args?.source || ''),
        vaultName: args?.vaultName ? String(args.vaultName) : null
      })

      const pool = requestedFolder
        ? search.pool.filter((note) => {
            const noteFolder = String(note.folder || '').trim().toLowerCase()
            const target = requestedFolder.toLowerCase()
            return noteFolder === target || noteFolder.startsWith(target + '/')
          })
        : search.pool

      const matches = buildNoteMatches(pool, q, { limit: lim, excerptLength: 200 })

      return {
        ok: true,
        count: matches.length,
        results: matches,
        ...(search.vaultResolution ? { vaultResolution: search.vaultResolution } : {})
      }
    }
  })

  registerTool({
    ...getSharedToolRegistration('create_note'),
    handler: async (args) => {
      try {
        const title = String(args?.title || '').slice(0, 200)
        const content = String(args?.content || '')
        if (!title || !content) return { ok: false, error: 'Missing title or content' }
        const draftValidation = validateCreateNoteDraft({ title, content })
        if (!draftValidation.ok) {
          return {
            ok: false,
            error: draftValidation.error,
            message: draftValidation.message
          }
        }
        const vis = String(args?.visibility || 'campaign').toLowerCase()
        const specific = Array.isArray(args?.specificUsers) ? args.specificUsers.map(String) : []
        const requestedVaultId = args?.vaultId ? String(args.vaultId) : null
        const requestedVaultNameRaw = args?.vaultName ? String(args.vaultName) : null
        const requestedVaultName = requestedVaultNameRaw ? normalizeVaultName(requestedVaultNameRaw) : null

        const cs = useCampaignStore()
        const userStore = useUserStore()
        const current =
          (typeof cs?.getCurrentCampaign === 'function'
            ? cs.getCurrentCampaign()
            : cs?.currentCampaign) || null
        const campaignId = current && (current.id || current.value?.id)
        const userId = userStore?.userId
        if (!userId) return { ok: false, error: 'Missing user' }

        const wantsVaultTarget = requestedVaultId || requestedVaultName

        // Campaign note path only when no explicit vault target was requested
        if (campaignId && !wantsVaultTarget) {
          const level =
            vis === 'public'
              ? PermissionLevel.PUBLIC
              : vis === 'private'
                ? PermissionLevel.PRIVATE
                : vis === 'user_specific'
                  ? PermissionLevel.USER_SPECIFIC
                  : PermissionLevel.CAMPAIGN

          const permission = PermissionService.createPermission(userId, level, specific, campaignId)
          const now = serverTimestamp()
          const payload = {
            title,
            content,
            permission,
            resourceType: 'notes',
            campaignId,
            createdAt: now,
            updatedAt: now
          }

          const ref = await addDoc(collection(db, 'campaigns', campaignId, 'notes'), payload)
          return { ok: true, id: ref.id, noteTitle: title, source: 'campaign' }
        }

        // Knowledge Base path when explicitly targeting a vault OR when no campaign is active
        const vs = useVaultStore()
        let vault = null
        try {
          await vs.loadVaults()
          const matchById = requestedVaultId
            ? vs.vaults.find((v) => v.id === requestedVaultId)
            : null
          const matchByNameResolution =
            !matchById && requestedVaultNameRaw ? resolveVaultByName(vs.vaults, requestedVaultNameRaw) : null
          const matchByName = matchByNameResolution?.vault || null
          const available = listAvailableVaultNames(vs.vaults)

          if (wantsVaultTarget) {
            if (matchByNameResolution?.ambiguous) {
              return {
                ok: false,
                error: 'vault_ambiguous',
                requestedVaultId,
                requestedVaultName: requestedVaultNameRaw || null,
                matchingVaults: listAvailableVaultNames(matchByNameResolution.matches),
                availableVaults: available
              }
            }

            vault = matchById || matchByName || null
            if (!vault) {
              return {
                ok: false,
                error: 'vault_not_found',
                requestedVaultId,
                requestedVaultName: requestedVaultNameRaw || null,
                availableVaults: available
              }
            }
          } else {
            vault = await vs.ensureDefaultVault({ userId })
          }

          const targetVaultId = vault?.id || vs.activeVaultId || null
          if (!targetVaultId) {
            return {
              ok: false,
              error: 'vault_unavailable',
              requestedVaultId,
              requestedVaultName: requestedVaultNameRaw || null,
              availableVaults: available
            }
          }

          let activationError = null
          if (vs.activeVaultId !== targetVaultId) {
            try {
              await vs.setActiveVault(targetVaultId)
            } catch (err) {
              activationError = err
            }
          }

          const created = await vs.createNote(
            {
              title,
              content,
              frontmatter: { permissions: 'PRIVATE' },
              permissions: 'PRIVATE',
              ownerId: userId
            },
            targetVaultId
          )
          return {
            ok: true,
            id: created.id,
            vaultId: targetVaultId,
            vaultName:
              vault?.name ||
              (Array.isArray(vs.vaults)
                ? vs.vaults.find((v) => v.id === targetVaultId)?.name
                : null) ||
              null,
            noteTitle: title,
            source: 'knowledgebase',
            activationError: activationError
              ? activationError?.message || 'set_active_vault_failed'
              : undefined
          }
        } catch (kbErr) {
          return { ok: false, error: kbErr?.message || 'create_kb_note_failed' }
        }
      } catch (e) {
        return { ok: false, error: e?.message || 'create_note_failed' }
      }
    }
  })

  registerTool({
    ...getSharedToolRegistration('update_note'),
    handler: async (args) => {
      try {
        const resolved = await resolveNoteTarget(args)
        if (!resolved?.ok) return resolved
        const noteId = String(resolved?.note?.id || '')
        if (!noteId) return { ok: false, error: 'Missing id' }

        const { appendText, replaceText, titlePatch, hasContentInput, mode } =
          parseNoteUpdateRequest(args)
        if (!titlePatch && !hasContentInput) return { ok: false, error: 'No fields to update' }

        const source = String(args?.source || resolved?.note?.source || '').toLowerCase()
        const vaultId = args?.vaultId ? String(args.vaultId) : resolved?.note?.vaultId || null

        if (vaultId || source === 'knowledgebase') {
          const vs = useVaultStore()
          try {
            await vs.loadVaults()
            // Find the vault containing the note if vaultId is missing or wrong
            let targetVaultId = vaultId || null
            if (!targetVaultId || !vs.vaults.some((v) => v.id === targetVaultId)) {
              // First try in-memory vault notes if present
              for (const vault of vs.vaults) {
                const note = Array.isArray(vault.notes)
                  ? vault.notes.find((n) => n.id === noteId)
                  : null
                if (note) {
                  targetVaultId = vault.id
                  break
                }
              }
              // If still unknown, probe each vault directly via NoteService.getNote
              if (!targetVaultId) {
                for (const vault of vs.vaults) {
                  try {
                    const fetched = await NoteService.getNote(vault.id, noteId)
                    if (fetched) {
                      targetVaultId = vault.id
                      break
                    }
                  } catch {
                    /* ignore and continue probing */
                  }
                }
              }
            }
            if (!targetVaultId || !vs.vaults.some((v) => v.id === targetVaultId)) {
              return { ok: false, error: 'Vault not found for note' }
            }
            // Always fetch authoritative note content
            let existingContent = null
            let existingTitle = null
            try {
              const fetched = await NoteService.getNote(targetVaultId, noteId)
              if (fetched) {
                if (typeof fetched.content === 'string') existingContent = fetched.content
                if (typeof fetched.title === 'string') existingTitle = fetched.title
              }
            } catch {
              /* ignore fetch errors */
            }
            const updates = buildNoteUpdateFields({
              existingContent,
              mode,
              appendText,
              replaceText,
              titlePatch
            })
            await NoteService.updateNote(targetVaultId, noteId, updates)
            await vs.loadAllVaultsNotes()
            const noteTitle = titlePatch || existingTitle || resolved?.note?.title || ''
            const vaultName =
              vs.vaults.find((v) => v.id === targetVaultId)?.name || resolved?.note?.vaultName || null
            return {
              ok: true,
              id: noteId,
              vaultId: targetVaultId,
              vaultName,
              noteTitle,
              source: 'knowledgebase'
            }
          } catch (e) {
            return { ok: false, error: e?.message || 'update_kb_note_failed' }
          }
        }

        const cs = useCampaignStore()
        const userStore = useUserStore()
        const current =
          (typeof cs?.getCurrentCampaign === 'function'
            ? cs.getCurrentCampaign()
            : cs?.currentCampaign) || null
        const campaignId = resolved?.note?.campaignId || (current && (current.id || current.value?.id))
        const userId = userStore?.userId
        if (!campaignId || !userId) return { ok: false, error: 'Missing campaign or user' }

        const ref = doc(db, 'campaigns', campaignId, 'notes', noteId)
        const snap = await getDoc(ref)
        if (!snap.exists()) return { ok: false, error: 'Note not found' }

        const data = snap.data()
        const owner = data?.permission?.owner
        if (owner && owner !== userId) {
          return { ok: false, error: 'Permission denied' }
        }

        const updates = buildNoteUpdateFields({
          existingContent: data?.content || '',
          mode,
          appendText,
          replaceText,
          titlePatch
        })
        updates.updatedAt = serverTimestamp()
        await updateDoc(ref, updates)
        const noteTitle = titlePatch || data?.title || resolved?.note?.title || ''
        return { ok: true, id: noteId, noteTitle, source: 'campaign' }
      } catch (e) {
        return { ok: false, error: e?.message || 'update_note_failed' }
      }
    }
  })
}
